import {
  BundleItemInput,
  BundleItemType as GqlBundleItemType,
  BundleStatus,
  CreateOtpBundleInput,
  UpdateOtpBundleInput,
} from 'generated/graphql';
import toast from 'react-hot-toast';
import { isEmpty } from 'lodash';
import {
  BundleFormValues,
  formDefaultValues,
  FormMode,
} from './otpBundles.model';
import {
  AllContent,
  AllPrograms,
  RawContentItem,
  RawOtpBundle,
  RawPrograms,
} from '../../common/bundle.types';
import { Program, BundleItemType } from '@playbook-technologies/common-enums';
import { GraphQLError } from 'graphql';
import { convertDollarsToCents } from '../../common/bundles.mapper';

export const validateContentForPublish = (
  programs: number[],
  features: number[],
  content: string[],
): string | undefined => {
  if (isEmpty(programs) && isEmpty(features) && isEmpty(content)) {
    return 'Offer must contain at least one item from following categories: Programs, Features, Content items';
  }
};

export const getBundleFormDataByBundleId = (
  bundleId: number,
  bundles: RawOtpBundle[],
  rawContent: RawContentItem[],
  allPrograms: AllPrograms,
  mode: FormMode,
) => {
  const bundle = bundles.find((b) => b.id === bundleId);
  if (!bundle) {
    toast.error('Could not find selected bundle');
    return formDefaultValues;
  }

  const name = mode === 'duplicate' ? `${bundle.title} Copy` : bundle?.title;
  const playbookFee =
    mode === 'edit' ? bundle?.oneTimeProduct?.playbookFee : undefined;

  return {
    name,
    price: bundle?.oneTimeProduct?.price
      ? String(bundle.oneTimeProduct.price / 100)
      : '',
    description: bundle?.description || undefined,
    features: (bundle?.items || [])
      .filter((i) => i.itemType === GqlBundleItemType.Feature)
      .map((i) => i.itemId) as number[],
    programs: toCopyProgramIds(mode, allPrograms, bundle?.items),
    content: toCopyContentIds(mode, rawContent, bundle?.items),
    playbookFee,
  };
};

const toCopyContentIds = (
  mode: FormMode,
  rawContent: RawContentItem[],
  bundleItems: RawOtpBundle['items'],
): string[] => {
  if (!bundleItems) return [];

  const contentItemsToCopy = bundleItems.filter(
    (i) => i.itemType === GqlBundleItemType.Content,
  );
  if (contentItemsToCopy.length === 0) return [];

  if (mode === 'edit') {
    return contentItemsToCopy.map((i) => i.contentId) as string[];
  }

  return contentItemsToCopy
    .filter((c) => {
      const contentItem = rawContent.find((i) => i.value.id === c.contentId);
      return !contentItem?.value.isArchived;
    })
    .map((i) => i.contentId) as string[];
};

const toCopyProgramIds = (
  mode: FormMode,
  allPrograms: AllPrograms,
  bundleItems: RawOtpBundle['items'],
): number[] => {
  if (!bundleItems) return [];

  const programsToCopy = bundleItems.filter(
    (i) => i.itemType === GqlBundleItemType.Program,
  );
  if (programsToCopy.length === 0) return [];

  if (mode === 'edit') {
    return programsToCopy.map((i) => i.itemId) as number[];
  }

  return programsToCopy
    .filter((p) => {
      const program = allPrograms.find((i) => i.id === p.itemId);
      return !program?.isArchived;
    })
    .map((i) => i.itemId) as number[];
};

export const toUpdateBundlePayload = (
  bundleId: number,
  formValues: BundleFormValues,
  bundleStatus: BundleStatus,
  isAdmin: boolean,
): UpdateOtpBundleInput => {
  const creatorsPayload = {
    id: bundleId,
    title: formValues.name,
    priceInCents: convertDollarsToCents(formValues.price),
    status: bundleStatus,
    description: formValues.description || null,
    items: toPayloadItems(formValues),
  };

  if (!isAdmin) return creatorsPayload;

  return {
    ...creatorsPayload,
    playbookFeePercent: formValues.playbookFee,
  };
};

export const toCreateBundlePayload = (
  influencerId: number,
  formValues: BundleFormValues,
  status?: BundleStatus,
): CreateOtpBundleInput => {
  return {
    influencerId,
    title: formValues.name,
    priceInCents: convertDollarsToCents(formValues.price),
    status: status || BundleStatus.Draft,
    description: formValues.description || null,
    items: toPayloadItems(formValues),
  };
};

const toPayloadItems = (formValues: BundleFormValues): BundleItemInput[] => {
  const content = (formValues.content || []).map((id) => ({
    contentId: id,
    itemType: GqlBundleItemType.Content,
  }));
  const programs = (formValues.programs || []).map((id) => ({
    itemId: id,
    itemType: GqlBundleItemType.Program,
  }));
  const features = (formValues.features || []).map((id) => ({
    itemId: id,
    itemType: GqlBundleItemType.Feature,
  }));

  return [...content, ...programs, ...features];
};

export const toAllOtpPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter((p) => p.accessType === Program.AccessType.Exclusive.value)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt && !p.archivedAt,
      isArchived: !!p.archivedAt,
    }));

export const toSellableOtpPrograms = (programs?: RawPrograms) =>
  (programs || [])
    .filter((p) => p.accessType === Program.AccessType.Exclusive.value)
    .filter((p) => !p.archivedAt)
    .map((p) => ({
      id: p.id,
      name: p.name || '',
      isPublished: !!p.publishedAt,
    }));

type ErrorDetail = {
  itemType: string;
  itemId?: number;
  contentId?: string;
  title?: string;
};
export type MutationErrors = Array<
  GraphQLError & {
    code?: string;
    details?: Array<ErrorDetail>;
  }
>;

export const getMutationErrors = (
  errors: MutationErrors,
  allPrograms: AllPrograms,
  allContent: AllContent,
): string | string[] =>
  errors.map((e) => {
    if (e?.code === 'OTP_BUNDLE_VALIDATION_ERROR') {
      const details = (e?.details || []).map(
        (d) => `• ${getItemTitle(d, allPrograms, allContent)}`,
      );
      return `${e.message}:\n${details.join('\n')}`;
    }
    return e.message;
  });

const notFound = 'not found';

const getItemTitle = (
  details: ErrorDetail,
  allPrograms: AllPrograms,
  allContent: AllContent,
) => {
  if (details.itemType === BundleItemType.PROGRAM) {
    const program = allPrograms.find((p) => p.id === details.itemId);
    return program?.name || notFound;
  }

  if (details.itemType === BundleItemType.CONTENT) {
    const contentItem = allContent.find((c) => c.id === details.contentId);
    return contentItem?.name || notFound;
  }

  return notFound;
};

export const toSavedProgramIds = (
  items: RawOtpBundle['items'] = [],
): number[] =>
  items
    .filter((i) => i.itemType === GqlBundleItemType.Program)
    .map((i) => i.itemId) as number[];
export const toSavedContentIds = (
  items: RawOtpBundle['items'] = [],
): string[] =>
  items
    .filter((i) => i.itemType === GqlBundleItemType.Content)
    .map((i) => i.contentId) as string[];
